import { Theme } from '@silvertours/front-shared';
import { GridConfiguration, GridLayout } from '../../types';

const GRID_COLUMNS = 12;
// Max width of the page content, see `Global.styles.tsx`
const CONTENT_MAX_WIDTH = 1220;
// Horizontal page gutter on each side, see `layoutContentOnlyPadding` in `Global.styles.tsx`
const GUTTER_BASE = 16;
const GUTTER_SM = 32;
// From `xxl` on the gutter is fixed and the content grows with the viewport
const GUTTER_XXL = 350;
// Grid gap below `lg` (`spacing[40]`) and from `lg` on (`spacing[50]`), see `LinkGrid.styles.tsx`
const GRID_GAP_BASE = 16;
const GRID_GAP_LG = 24;
// Min height of a grid row, see `Anchor` in `LinkGrid.styles.tsx`
const ROW_HEIGHT_BASE = 164;
const ROW_HEIGHT_MD = 200;
const ROW_HEIGHT_XL = 250;

const getColumns = (value: GridConfiguration) =>
  typeof value === 'number' ? value : value[0];

const getRows = (value: GridConfiguration) =>
  typeof value === 'number' ? 1 : value[1];

/**
 * Width of the image when `object-fit: cover` scales it to the height of the
 * row span. A wide image in a tall or square tile is wider than the tile.
 */
const getCoverWidth = (
  value: GridConfiguration,
  rowHeight: number,
  gap: number,
  aspectRatio: number,
) => {
  const rows = getRows(value);

  return Math.ceil((rows * rowHeight + (rows - 1) * gap) * aspectRatio);
};

// Width of a span of columns, including the gaps inside the span
const getSpanWidthXl = (value: GridConfiguration) => {
  const columns = getColumns(value);
  const columnWidth =
    (CONTENT_MAX_WIDTH - (GRID_COLUMNS - 1) * GRID_GAP_LG) / GRID_COLUMNS;

  return columns * columnWidth + (columns - 1) * GRID_GAP_LG;
};

/**
 * Width of a span of columns in a fluid container, as a CSS `calc()`. With
 * the container width `c`, a span of `n` columns is `n / 12 * (c + gap) - gap`.
 * The `(100vw` keeps Next.js from reading a share of the viewport out of the
 * value, so it gives all image widths in the `srcset`.
 */
const getSpanWidth = (value: GridConfiguration, gutter: number, gap: number) =>
  `calc((100vw - ${2 * gutter - gap}px) * ${getColumns(value)} / ${GRID_COLUMNS} - ${gap}px)`;

// Image width in a fluid container: the larger of the span width and the cover width
const getFluidWidth = (
  value: GridConfiguration,
  gutter: number,
  gap: number,
  rowHeight: number,
  aspectRatio?: number,
) => {
  const spanWidth = getSpanWidth(value, gutter, gap);

  if (!aspectRatio) {
    return spanWidth;
  }

  return `max(${spanWidth}, ${getCoverWidth(value, rowHeight, gap, aspectRatio)}px)`;
};

const getFixedWidth = (value: GridConfiguration, aspectRatio?: number) => {
  const spanWidth = Math.ceil(getSpanWidthXl(value));

  if (!aspectRatio) {
    return spanWidth;
  }

  return Math.max(
    spanWidth,
    getCoverWidth(value, ROW_HEIGHT_XL, GRID_GAP_LG, aspectRatio),
  );
};

/**
 * Builds the `sizes` attribute of a grid image from its layout. Below `xl`
 * and from `xxl` on the image width is the width of the column span in the
 * viewport without the page gutters, from `xl` to `xxl` it is the width of
 * the column span in the max content width. With the aspect ratio of the
 * image, the width is at least the width of the image when it covers the
 * height of the row span.
 */
export const getGridImageSizes = (
  [base, responsive = {}]: GridLayout,
  aspectRatio?: number,
) => {
  const { md, lg, xl } = responsive;
  const mdValue = md ?? base;
  const lgValue = lg ?? mdValue;
  const xlValue = xl ?? lgValue;

  return [
    `(min-width: ${Theme.getBreakpoint('xxl')}px) ${getFluidWidth(xlValue, GUTTER_XXL, GRID_GAP_LG, ROW_HEIGHT_XL, aspectRatio)}`,
    `(min-width: ${Theme.getBreakpoint('xl')}px) ${getFixedWidth(xlValue, aspectRatio)}px`,
    `(min-width: ${Theme.getBreakpoint('lg')}px) ${getFluidWidth(lgValue, GUTTER_SM, GRID_GAP_LG, ROW_HEIGHT_MD, aspectRatio)}`,
    `(min-width: ${Theme.getBreakpoint('md')}px) ${getFluidWidth(mdValue, GUTTER_SM, GRID_GAP_BASE, ROW_HEIGHT_MD, aspectRatio)}`,
    `(min-width: ${Theme.getBreakpoint('sm')}px) ${getFluidWidth(base, GUTTER_SM, GRID_GAP_BASE, ROW_HEIGHT_BASE, aspectRatio)}`,
    getFluidWidth(
      base,
      GUTTER_BASE,
      GRID_GAP_BASE,
      ROW_HEIGHT_BASE,
      aspectRatio,
    ),
  ].join(', ');
};
